/**
 * E2EE crypto helpers for license image upload/download.
 * All cryptographic operations run entirely in the browser (Web Crypto API).
 * The server never sees unencrypted data or plain-text keys.
 *
 * Cryptographic scheme (per spec Bildverschlüsselung.md):
 *   - File payload:  AES-GCM 256-bit (AEAD)
 *   - Key wrapping:  RSA-OAEP 2048-bit / SHA-256
 *   - IV:            12 bytes, crypto-random per upload
 *
 * Private key lifecycle:
 *   - Generated once in Profile, immediately downloaded as PKCS8 PEM file.
 *   - Never stored in the browser. The user keeps it on a USB stick.
 *   - For each viewing/sharing operation the user selects the file manually.
 */

// ─── Safe base64 helpers ──────────────────────────────────────────────────────
// Spreading large Uint8Arrays into String.fromCharCode() exceeds the JS engine's
// maximum argument count for multi-MB camera images. Use a chunked loop instead.

const CHUNK = 8192;

function bufferToBase64(buffer) {
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
    }
    return btoa(binary);
}

function base64ToBuffer(b64) {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

// ─── Key Generation ───────────────────────────────────────────────────────────

/**
 * Generate an RSA-OAEP 2048-bit key pair.
 * Both keys are extractable so the private key can be downloaded.
 * Key usages: publicKey → wrapKey, privateKey → unwrapKey.
 */
export async function generateKeyPair() {
    return window.crypto.subtle.generateKey(
        {
            name: 'RSA-OAEP',
            modulusLength: 2048,
            publicExponent: new Uint8Array([1, 0, 1]),
            hash: 'SHA-256',
        },
        true, // extractable – needed to download private key
        ['wrapKey', 'unwrapKey']
    );
}

// ─── Public Key Export / Import ───────────────────────────────────────────────

/**
 * Export the public key as SPKI base64 (stored server-side).
 */
export async function exportPublicKey(publicKey) {
    const spki = await window.crypto.subtle.exportKey('spki', publicKey);
    return bufferToBase64(spki);
}

/**
 * Import a SPKI base64 public key (fetched from server) for wrapping.
 */
export async function importPublicKey(spkiBase64) {
    return window.crypto.subtle.importKey(
        'spki',
        base64ToBuffer(spkiBase64),
        { name: 'RSA-OAEP', hash: 'SHA-256' },
        false,
        ['wrapKey']
    );
}

// ─── Private Key Download / Import ────────────────────────────────────────────

/**
 * Export and download the private key as a PKCS8 PEM file.
 * The user should save this on a USB stick.
 */
export async function downloadPrivateKey(privateKey, filename = 'fwminv-private.key') {
    const pkcs8 = await window.crypto.subtle.exportKey('pkcs8', privateKey);
    const b64 = bufferToBase64(pkcs8);
    const pem = [
        '-----BEGIN PRIVATE KEY-----',
        ...b64.match(/.{1,64}/g),
        '-----END PRIVATE KEY-----',
    ].join('\n');

    const blob = new Blob([pem], { type: 'application/x-pem-file' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

/**
 * Import a private key from a PEM or raw PKCS8 file (File object or text string).
 * Returns a CryptoKey usable for unwrapKey.
 */
export async function importPrivateKeyFromFile(fileOrText) {
    let pem;
    if (typeof fileOrText === 'string') {
        pem = fileOrText;
    } else {
        pem = await fileOrText.text();
    }

    // Strip PEM headers and whitespace
    const b64 = pem
        .replace(/-----BEGIN PRIVATE KEY-----/, '')
        .replace(/-----END PRIVATE KEY-----/, '')
        .replace(/\s+/g, '');

    return window.crypto.subtle.importKey(
        'pkcs8',
        base64ToBuffer(b64),
        { name: 'RSA-OAEP', hash: 'SHA-256' },
        false,
        ['unwrapKey']
    );
}

// ─── File Encryption / Decryption ─────────────────────────────────────────────

/**
 * Encrypt a file buffer with AES-GCM-256.
 * Returns { ciphertextBase64, ivBase64, aesKey }.
 * aesKey is extractable so it can be wrapped immediately after.
 */
export async function encryptFile(fileArrayBuffer) {
    const aesKey = await window.crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        true, // must be extractable for wrapKey
        ['encrypt', 'decrypt']
    );
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        aesKey,
        fileArrayBuffer
    );
    return {
        ciphertextBase64: bufferToBase64(ciphertext),
        ivBase64: bufferToBase64(iv),
        aesKey,
    };
}

/**
 * Decrypt ciphertext (base64) with AES-GCM-256.
 * Throws if the auth tag is invalid (AEAD guarantee).
 * Returns an ArrayBuffer.
 */
export async function decryptFile(ciphertextBase64, ivBase64, aesKey) {
    return window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: base64ToBuffer(ivBase64) },
        aesKey,
        base64ToBuffer(ciphertextBase64)
    );
}

// ─── Key Wrapping / Unwrapping ────────────────────────────────────────────────

/**
 * Wrap an AES key with an RSA-OAEP public key.
 * Returns base64-encoded envelope.
 */
export async function wrapAesKey(aesKey, recipientPublicKey) {
    const wrapped = await window.crypto.subtle.wrapKey(
        'raw',
        aesKey,
        recipientPublicKey,
        { name: 'RSA-OAEP' }
    );
    return bufferToBase64(wrapped);
}

/**
 * Unwrap an AES key from a base64 envelope using the private key.
 * Returns a CryptoKey (AES-GCM).
 *
 * @param {object} [opts]
 * @param {boolean} [opts.extractable=false]  make the key extractable (needed to
 *                                            re-wrap it for another user when sharing)
 * @param {string[]} [opts.usages=['decrypt']] key usages
 */
export async function unwrapAesKey(envelopeBase64, privateKey, opts = {}) {
    const { extractable = false, usages = ['decrypt'] } = opts;
    return window.crypto.subtle.unwrapKey(
        'raw',
        base64ToBuffer(envelopeBase64),
        privateKey,
        { name: 'RSA-OAEP' },
        { name: 'AES-GCM', length: 256 },
        extractable,
        usages
    );
}

// ─── Text (short string) Encryption / Decryption ──────────────────────────────
// Used for the driver's license number – same scheme as the file payload,
// just with a UTF-8 string instead of a file buffer.

/**
 * Encrypt a plain string with a fresh AES-GCM-256 key.
 * Returns { ciphertextBase64, ivBase64, aesKey } (aesKey extractable for wrapping).
 */
export async function encryptText(plainText) {
    const data = new TextEncoder().encode(plainText);
    return encryptFile(data.buffer);
}

/**
 * Decrypt a base64 ciphertext back into a string.
 */
export async function decryptText(ciphertextBase64, ivBase64, aesKey) {
    const plainBuffer = await decryptFile(ciphertextBase64, ivBase64, aesKey);
    return new TextDecoder().decode(plainBuffer);
}

/**
 * Re-encrypt a string with an existing AES-GCM key (new IV).
 * Used when editing the license number without changing the envelopes.
 */
export async function encryptTextWithKey(plainText, aesKey) {
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        aesKey,
        new TextEncoder().encode(plainText)
    );
    return { ciphertextBase64: bufferToBase64(ciphertext), ivBase64: bufferToBase64(iv) };
}
